import React from 'react';
import { Box, Typography } from '@mui/material';
import { DS } from '../PatientForms/designTokens';

type Option<T extends string | number | boolean> = {
  label: string;
  value: T;
};

type Props<T extends string | number | boolean> = {
  id?: string;
  label: string;
  name: string;
  value?: T;
  options: Option<T>[];
  onChange: (val: T) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  labelFontWeight?: number;
  labelFontSize?: string;
};

export function FormRadioGroup<T extends string | number | boolean>({
  id,
  label,
  name,
  value,
  options,
  onChange,
  onBlur,
  labelFontWeight = 600,
  labelFontSize = '16px',
}: Props<T>) {
  return (
    <Box id={id} sx={{ mb: 3 }}>
      <Typography
        sx={{
          fontWeight: labelFontWeight,
          color: '#374151',
          mb: 1.5,
          fontFamily: DS.fontBody,
          fontSize: labelFontSize,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        {options.map((opt) => {
          const checked = value !== undefined && value === opt.value;
          return (
            <Box
              key={String(opt.value)}
              component="label"
              sx={{
                px: 1.5,
                py: 0.75,
                borderRadius: '8px',
                border: '1px solid',
                borderColor: checked ? DS.deepTeal : '#D1D5DB',
                backgroundColor: checked ? DS.deepTeal : '#fff',
                color: checked ? '#fff' : '#374151',
                cursor: 'pointer',
                fontSize: '14px',
                fontFamily: DS.fontBody,
                transition: 'all 0.15s',
                userSelect: 'none',
                '&:hover': checked
                  ? {}
                  : { borderColor: DS.deepTeal, color: DS.deepTeal },
              }}
            >
              <input
                type="radio"
                name={name}
                value={String(opt.value)}
                checked={checked}
                onChange={() => onChange(opt.value)}
                onBlur={onBlur}
                style={{
                  position: 'absolute',
                  width: 0,
                  height: 0,
                  opacity: 0,
                }}
              />
              {opt.label}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
